import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, share, shareReplay, switchMap } from 'rxjs/operators';

import { CurrentUser, Organisation, Role, UserInfo, UserRoleType } from '@digihaven/shared';

import { ErrorMessages } from '@common/entities/error/error-messages.entity';
import { Functionalities } from '@common/entities/functionalities.entity';

import { ApplicationState } from 'state';

import { HttpService } from '../http/http.service';

@Injectable()
export class AuthorizationService {
    private readonly userInfoUrl: string;
    private userInfo$!: Observable<CurrentUser>;

    constructor(
        private store: Store<ApplicationState>,
        private httpService: HttpService
    ) {
        this.userInfoUrl = `users/current`;

        this.buildSourceStream();
    }

    getFunctionalities(): Observable<Functionalities> {
        return this.userInfo$.pipe(map(rawObject => this.extractFunctionalities(rawObject)));
    }

    getUserInfo(): Observable<UserInfo> {
        return this.userInfo$.pipe(map(this.extractUserInfo.bind(this)));
    }

    getOrganisation(): Observable<Organisation> {
        return this.userInfo$.pipe(map(this.extractOrganisation.bind(this)));
    }

    private buildSourceStream(): void {
        this.userInfo$ = this.store
            .select(state => state.token.userTokens.access_token)
            .pipe(
                filter(token => !!token),
                switchMap(this.fetchUserInfo.bind(this)),
                share()
            );
    }

    fetchUserInfo(): Observable<CurrentUser> {
        return this.httpService.get<CurrentUser>(this.userInfoUrl).pipe(shareReplay());
    }

    private extractUserInfo(currentUser: CurrentUser): UserInfo {
        return {
            firstName: currentUser.FirstName,
            lastName: currentUser.LastName,
            username: currentUser.Login,
            roleLabel: currentUser.Roles[0].Label as UserRoleType
        };
    }

    private extractOrganisation(currentUser: CurrentUser): Organisation {
        const {
            Id,
            ExternalId,
            Name,
            LongName,
            CustomProperties: { ApplicationScope, Modules, IsSelectionCommission }
        } = currentUser.Organisation;

        return {
            Id: Id.toString(),
            ExternalId,
            Name,
            LongName,
            ApplicationScope,
            Modules,
            IsSelectionCommission
        };
    }

    private extractFunctionalities(currentUser: CurrentUser): Functionalities {
        const roles: Role[] = currentUser.Roles;
        if (roles.length === 0) {
            throw Error(ErrorMessages.NO_FUNCTIONALITIES);
        }

        const functions: string[] = roles[0].Functions.map(val => val.toLowerCase());
        const fns: Functionalities = {
            editSeries: functions.includes('edit_series'),
            createSeries: functions.includes('create_series'),
            createTemplateSeries: functions.includes('create_template_series'),
            importSeries: functions.includes('import_series'),
            submitSeries: functions.includes('submit_series'),
            assignSeriesForEvaluation: functions.includes('assign_series_for_evaluation'),
            evaluateSeries: functions.includes('evaluate_series'),
            writeSeriesConclusion: functions.includes('write_series_conclusion'),
            closeSeries: functions.includes('close_series'),
            approveClosingSeries: functions.includes('approve_closing_series'),
            exportPublishedSeriesInformationManagementPlansSelectionList: functions.includes(
                'export_publishedseries_informationmanagementplans_selectionlists'
            ),
            initializeSeriesReview: functions.includes('initialize_series_review'),
            deleteSeries: functions.includes('delete_series'),
            viewGroups: functions.includes('view_groups'),
            adminOrganisation: functions.includes('edit_admin_settings'),
            adminGroups: functions.includes('admin_groups'),
            adminSecurity: functions.includes('admin_security'),
            adminExtensions: functions.includes('admin_extensions'),
            adminFieldDefinitions: functions.includes('admin_field_definitions'),
            editRelations: functions.includes('edit_relations'),
            viewSeries: functions.includes('view_series'),
            viewUnpublishedSeries: functions.includes('view_unpublished_series'),
            viewReports: functions.includes('view_reports'),
            createDossiers: functions.includes('create_dossier')
        };

        return fns;
    }
}
